import React, { useState, useRef, useEffect, forwardRef } from 'react';
import cn from 'classnames';
import utils from '@ehi/global-marketing-interface/utils';
import { scrollLock } from 'utils/scrollLock';
import { safeExecute } from 'utils/validation';
import { formatKeys } from 'utils/accessibility/keys';
import { i18n } from 'utils/i18n';
import { uuid } from 'utils/dom';
import useClickOutside from 'hooks/useClickOutside';
import { usePrevious } from 'hooks/usePrevious';
import { SESSION_STORAGE } from 'constants/session';
import ToolTip from 'components/ToolTip';
import FieldControl from 'components/Form/FieldControl';
import Button from 'components/Button';
import { propTypes } from './ResponsiveListboxPropTypes';

const ResponsiveListbox = forwardRef((props, ref) => {
  const {
    id,
    expanded,
    optionsGroups,
    options,
    suggestedOption,
    value,
    countrySelector,
    onChange,
    onFocus,
    onBlur,
    onToggle,
    showGuidedError,
    renderGuidedError,
    timeAndDatePickerFullHeight,
    asterisk,
    required,
    mobileLabel,
    label,
    fullLabel,
    breakpoint,
    isInvalidDateModalOpen,
    driversAge,
    isDriverAgeUpdated,
    lastUpdated,
    time,
    name,
    useMobileVersionForTablet,
    currentPageTitle,
    type,
    guided,
    isFocusedActive,
  } = props;

  const [uniqueId] = useState(id);

  // Helper function from class method: this._flattenOptionsGroups()
  const flattenOptionsGroups = () => {
    const flat = [];
    optionsGroups.forEach(({ name: groupName, options: groupOptions, ...group }) => {
      flat.push({ name: groupName, isSeparator: true, ...group });
      flat.push(...groupOptions.map((option) => ({ ...option, name: groupName })));
    });
    return flat;
  };

  const [flatOptions, setFlatOptions] = useState(() => {
    if (utils.isArrayNotEmpty(optionsGroups)) return flattenOptionsGroups();
    if (utils.isArrayNotEmpty(options)) return options;
    return [];
  });

  const [isExpanded, setIsExpanded] = useState(expanded);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [currentSuggestedOption] = useState(suggestedOption || null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [characterSearchKey, setCharacterSearchKey] = useState('');
  const [characterSearchIndex, setCharacterSearchIndex] = useState(0);
  const [interactive, setInteractive] = useState(false);
  const [followCursor, setFollowCursor] = useState(false);

  // Refs
  const togglerRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const clickOutsideRef = useRef(null);
  const highlightedOptionRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  const prevProps = usePrevious(props);

  //  We can't use deep compare here due to the option groups containing a react comp tooltip, this throws an
  //  error.  Instead we compare the lengths.
  const compareLengthsOfOptionGroups = (currentOptionGroups, prevOptionGroups) => {
    if (currentOptionGroups.length !== prevOptionGroups.length) return true;

    for (let i = 0; i < currentOptionGroups.length; i += 1) {
      const currentOptionsLength = currentOptionGroups[i].options.length;
      const prevOptionsLength = prevOptionGroups[i].options.length;
      if (currentOptionsLength !== prevOptionsLength) return true;
    }

    return false;
  };

  //  TODO: move this out of listbox, we need this due to the reference changing from getting hours
  const lengthComparsionOfPropsIfUnavailableLocationModalIsOpen = (
    currentOptionGroupsProps,
    previousOptionGroupsProps
  ) => {
    if (isInvalidDateModalOpen) {
      return compareLengthsOfOptionGroups(currentOptionGroupsProps, previousOptionGroupsProps);
    }
    return currentOptionGroupsProps !== previousOptionGroupsProps;
  };

  /**
   * @private
   * @returns {Number} Returns index of option from value
   */
  const getSelectedIndexFromValueOrLabel = (val) =>
    flatOptions.findIndex((option) => option.value?.toString() === val?.toString());

  const setSelectedLabelFromValue = (val) => {
    const stringValue = val?.toString() || val;
    const selectedOption = flatOptions.find((option) => option.value?.toString() === stringValue);
    setSelectedLabel(selectedOption ? selectedOption.label : val);
  };

  /**
   * Scrolls scrollable dropdowns to the option by index
   *
   * @param index {Number}
   */
  const scrollToOption = (element) => {
    if (element && listRef.current) {
      listRef.current.scrollTop = element.offsetTop;
      listRef.current.dispatchEvent(new CustomEvent('scroll'));
    }
  };

  /**
   * Highlights Option Item by Index
   *
   * @param index {Number}
   */
  const highlightOption = (index, shouldScrollToOption = false) => {
    setHighlightedIndex(index);
    if (shouldScrollToOption) {
      setTimeout(() => {
        if (highlightedOptionRef.current) {
          scrollToOption(highlightedOptionRef.current);
        }
      }, 0);
    }
  };

  /**
   * Highlights the previous sibling option of currently highlighted item
   */
  const highlightPrev = () => {
    if (!flatOptions.length) return;
    let prevIndex = highlightedIndex > 0 ? highlightedIndex - 1 : flatOptions.length - 1;
    let option = flatOptions[prevIndex];
    while (option.disabled || (option.isSeparator && !option.tooltip)) {
      prevIndex = prevIndex > 0 ? prevIndex - 1 : flatOptions.length - 1;
      option = flatOptions[prevIndex];
    }
    highlightOption(prevIndex, true);
  };

  /**
   * Highlights the next sibling option of currently highlighted item
   */
  const highlightNext = () => {
    if (!flatOptions.length) return;
    let nextIndex = highlightedIndex < flatOptions.length - 1 ? highlightedIndex + 1 : 0;
    let option = flatOptions[nextIndex];
    while (option.disabled || (option.isSeparator && !option.tooltip)) {
      nextIndex = nextIndex < flatOptions.length - 1 ? nextIndex + 1 : 0;
      option = flatOptions[nextIndex];
    }
    highlightOption(nextIndex, true);
  };

  /**
   * Selects an option by value
   *
   * @param value {any}
   */
  const selectOptionByValue = (val) => {
    safeExecute(onChange, val);
    setSelectedLabelFromValue(val);
    const valueIndex = getSelectedIndexFromValueOrLabel(val);
    if (valueIndex > -1) highlightOption(valueIndex);
  };

  const handleScrollPage = () => {
    if (followCursor || interactive) {
      setFollowCursor(false);
      setInteractive(false);
    }
  };

  useEffect(() => {
    if (!countrySelector) selectOptionByValue(value);
    window.addEventListener('scroll', handleScrollPage);
    return () => window.removeEventListener('scroll', handleScrollPage);
  }, []);

  // when the options change, confirm that the selected value is among them and not disabled
  // if it is not, default to the option with "selected: true" or '' (empty string)
  const confirmSelectedValueInOptions = (currentFlatOptions = flatOptions) => {
    const stringValue = value?.toString() || value;
    const selectedOption =
      currentFlatOptions
        .find(({ disabled, value: optionValue }) => !disabled && optionValue?.toString() === stringValue)
        ?.value?.toString() ||
      currentFlatOptions.find((option) => option.selected)?.value?.toString() ||
      '';
    selectOptionByValue(selectedOption);
  };

  useEffect(() => {
    if (prevProps) {
      // If default value changed update list to select the options with this new default value
      // comparing time and value if it is not umatched calling the setOptionByalue  function to update the time value
      if (
        value !== prevProps?.value ||
        (lastUpdated && lastUpdated !== prevProps?.lastUpdated) ||
        (time && value.length <= 0 && time !== value && time !== prevProps?.time)
      ) {
        let selectedOption = value;
        if (time && value.length <= 0 && time !== value && (time !== prevProps?.time || time !== value)) {
          selectedOption = time;
        }

        if (selectedOption !== prevProps?.value) {
          setTimeout(() => {
            selectOptionByValue(selectedOption);
          }, 0);
        }
      }

      if (driversAge && isDriverAgeUpdated && isDriverAgeUpdated !== prevProps?.isDriverAgeUpdated) {
        setTimeout(() => {
          selectOptionByValue(driversAge);
        }, 0);
      }

      //  If InvalidLocationModal is open we do a length comparison due to new hours coming in from the time validation request
      if (lengthComparsionOfPropsIfUnavailableLocationModalIsOpen(optionsGroups, prevProps?.optionsGroups)) {
        // flatten options groups here and store flat array in state
        if (utils.isArrayNotEmpty(optionsGroups)) {
          const newOptions = flattenOptionsGroups();
          setFlatOptions([...newOptions]);
          confirmSelectedValueInOptions([...newOptions]);
        }
      }

      if (options !== prevProps?.options) {
        utils.isArrayNotEmpty(options) && setFlatOptions([...options]);
        confirmSelectedValueInOptions([...options]);
      }
    }
  }, [prevProps, isDriverAgeUpdated, value, options, driversAge, optionsGroups, time, lastUpdated]);

  /**
   * Checks to see if the click triggered by the `handleClickOutside` function is just on the tooltip,
   * in this case we close the tooltip but keep the dropdown open, We are comparing it to a string,
   * because putting a value in a data- attribute changes it to a string
   */
  const didClickOnToolTip = (clickPath) => clickPath?.findIndex((node) => node?.dataset?.isTooltip === 'true') !== -1;

  // --- Action methods ---

  const showDropdown = () => {
    const isMobileMode = breakpoint.isMobile || (breakpoint.isTablet && useMobileVersionForTablet);
    setIsExpanded(true);
    // Give state update time to reflect before accessing DOM or state-dependent logic
    setTimeout(() => {
      listRef.current.focus();
      listRef.current.dispatchEvent(new CustomEvent('scroll'));

      if (highlightedIndex > -1) {
        highlightOption(highlightedIndex, true);
      } else if (highlightedIndex < 0 && currentSuggestedOption) {
        const suggestedIndex = getSelectedIndexFromValueOrLabel(currentSuggestedOption);
        if (suggestedIndex > -1) {
          highlightOption(suggestedIndex, true);
        }
      }

      if (isMobileMode) {
        scrollLock();
      }
    }, 0);
  };

  const hideDropdown = () => {
    const isMobileMode = breakpoint.isMobile || (breakpoint.isTablet && useMobileVersionForTablet);
    setIsExpanded(false);
    setCharacterSearchKey('');
    setCharacterSearchIndex(0);
    setHighlightedIndex(getSelectedIndexFromValueOrLabel(value));

    if (isMobileMode) {
      scrollLock({ toggleValue: false });
    }
  };

  /**
   * handleClickOutside - Callback utilized by `withClickOutside` enhancer component - set's correct component state when focus is lost
   *
   * @returns {type} Description
   */
  const handleClickOutside = (e) => {
    const eventPath = e?.path || (e.composedPath && e?.composedPath());
    if (isExpanded && !didClickOnToolTip(eventPath)) {
      hideDropdown();
    }
  };

  /**
   * return true if FocusEvent (focus/blur) relatedTarget is contained in our wrapper
   */
  const isRelatedTargetContained = (el) => clickOutsideRef.current && clickOutsideRef.current.contains(el);

  /**
   * Handle Focus on Button or Dropdown List
   */
  const handleFocus = (e) => {
    if (!isRelatedTargetContained(e.nativeEvent.relatedTarget)) {
      onFocus && onFocus(e);
      // Force show dropdown if input is focused and is active or have errors.
      // Focus is triggered faster than showGuidedError changes so we have to delay to check it.
      setTimeout(() => {
        if (isFocusedActive || showGuidedError) showDropdown();
      }, 0);
    }
  };

  /**
   * Handle Blur on Button or Dropdown List
   */
  const handleBlur = (e) => {
    if (!isRelatedTargetContained(e.nativeEvent.relatedTarget)) {
      onBlur && onBlur(e);
    }
  };

  /**
   * Handle toggle show/hide options dropdown
   */
  const handleToggle = (e) => {
    e.preventDefault();
    safeExecute(onToggle, {
      isExpanded,
      selectedId,
      selectedLabel,
      highlightedIndex,
      flatOptions,
      characterSearchKey,
      characterSearchIndex,
      interactive,
      followCursor,
    });
    isExpanded ? hideDropdown() : showDropdown();
  };

  /**
   * Handles selecting currently highlighted option
   */
  const handleSelect = (e) => {
    e.preventDefault();
    const { value: selectedValue } = e.currentTarget.dataset;
    selectOptionByValue(selectedValue);
    sessionStorage.setItem(SESSION_STORAGE.USER_AGE_SELECTED, true);
    hideDropdown();
    // blur the input on mobile after selecting a value
    const isMobileMode = breakpoint.isMobile || (breakpoint.isTablet && useMobileVersionForTablet);
    if (isMobileMode) {
      listRef.current.blur();
    }
  };

  /**
   * Handles Mouse Over Highlighting Options
   */
  const handleHighlight = (index) => () => highlightOption(index);

  /**
   * Scroll handler to help determine whether there is scrolling up or down to show scroll indicators
   * TODO: Prevent Background from Scrolling on Mobile (Lock scrolling)
   */
  const handleScroll = () => {
    const { offsetHeight, scrollHeight, scrollTop } = listRef.current;
    const dropdownNodeClassList = dropdownRef.current?.classList;

    if (!interactive || !followCursor) {
      setFollowCursor(true);
      setInteractive(true);
    }

    if (scrollHeight) {
      if (scrollTop > 0) dropdownNodeClassList?.add('has-scroll-up');
      else dropdownNodeClassList?.remove('has-scroll-up');

      if (scrollTop !== scrollHeight - offsetHeight) dropdownNodeClassList?.add('has-scroll-down');
      else dropdownNodeClassList?.remove('has-scroll-down');
    }
  };

  /**
   * Handles the both mouseenter and mouseleave events of the scrolling arrows that appear on the dropdown
   * @dev this is meant for desktop interaction only
   */
  const handleMouseEnterLeaveScroll = (e) => {
    const { offsetHeight, scrollHeight, scrollTop } = listRef.current;
    const { type: eventType, currentTarget } = e;
    const { direction } = currentTarget.dataset;
    // On mousing over the arrows
    if (eventType === 'mouseenter') {
      if (direction === 'up') {
        scrollIntervalRef.current = setInterval(() => {
          if (scrollTop > 0) {
            listRef.current.scrollTop -= 10;
          }
        }, 50);
      }
      // If mouse over down arrow, start scrolling down until it reaches the end
      if (direction === 'down') {
        scrollIntervalRef.current = setInterval(() => {
          if (scrollTop < scrollHeight - offsetHeight) {
            listRef.current.scrollTop += 10;
          }
        }, 50);
      }
    }
    // When mouse leaves the arrow stop scrolling by clearing the interval
    if (eventType === 'mouseleave' && scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  /**
   * Finds first option that matches key entered text
   *
   * @param key {Number} the keycode
   * @returns Node | undefined {Object}
   */
  const findOption = (key) => {
    const character = String.fromCharCode(key);
    let newIndex = 0;

    if (characterSearchKey === character) {
      newIndex = characterSearchIndex + 1;
    }

    const foundIndex = flatOptions
      .slice(newIndex)
      .findIndex(
        (option) => !option.isSeparator && !option.disabled && RegExp(character).test(option.label[0].toUpperCase())
      );

    newIndex = foundIndex > -1 ? foundIndex + newIndex : -1;

    setCharacterSearchKey(character);
    setCharacterSearchIndex(newIndex);

    return newIndex;
  };

  /**
   * Opens tooltip in separator
   */
  const openTooltip = (node) => {
    const tooltip = node?.querySelector('button');
    tooltip && tooltip.click();
  };

  const handleKeyDown = (e) => {
    const key = e.which || e.keyCode;
    const a11yKeys = formatKeys(e);
    const isA11yKeys = Object.values(a11yKeys).some(Boolean);
    const isAlphanumericKeys = /[a-zA-Z0-9-_ ]/.test(String.fromCharCode(key));
    const role = e.currentTarget.getAttribute('role');
    // If dropdown is closed while focus is on the toggler
    if (!isExpanded && (isA11yKeys || isAlphanumericKeys)) {
      if (
        !a11yKeys.tab &&
        !a11yKeys.shiftTab &&
        !a11yKeys.shift &&
        !a11yKeys.escape &&
        !a11yKeys.arrowLeft &&
        !a11yKeys.arrowRight
      ) {
        e.preventDefault();
        showDropdown();

        if (a11yKeys.arrowUp) highlightPrev();
        else if (a11yKeys.arrowDown) highlightNext();
        else if (a11yKeys.home) highlightOption(0);
        else if (a11yKeys.end) highlightOption(flatOptions.length - 1);
        else if (isAlphanumericKeys) {
          const matchedIndex = findOption(key);
          matchedIndex > -1 && highlightOption(matchedIndex, true);
        }
      }
    }

    // If dropdown expanded while focused
    // TODO: Add tab focus on tooltips if it exists
    if (isExpanded && role === 'listbox') {
      if (isA11yKeys || isAlphanumericKeys) e.preventDefault();
      const option = flatOptions[highlightedIndex];

      switch (true) {
        case a11yKeys.arrowUp:
          // Highlight and scroll to previous item
          highlightPrev();
          break;
        case a11yKeys.arrowDown:
          // Highlight and scroll to next item
          highlightNext();
          break;
        case a11yKeys.home:
          // Highlight first item
          highlightOption(0, true);
          break;
        case a11yKeys.end:
          // Highlight last item
          highlightOption(flatOptions.length - 1, true);
          break;
        case a11yKeys.escape:
        case a11yKeys.shiftTab:
        case a11yKeys.tab:
          // If escaping while focused on dropdown, refocus on Toggler before closing it
          e.stopPropagation();
          togglerRef.current.focus();
          hideDropdown();
          break;
        case a11yKeys.enter:
        case a11yKeys.space:
          if (option && !option.isSeparator) {
            // Focus on togglers
            !guided && togglerRef.current.focus();
            // Select current highlighted item and close Dropdown
            selectOptionByValue(option.value);
            hideDropdown();
          }
          // If it has tooltip, open it
          option && option.tooltip && openTooltip(highlightedOptionRef.current);
          break;
        default:
          if (isAlphanumericKeys) {
            e.stopPropagation();
            const matchedIndex = findOption(key);
            matchedIndex > -1 && highlightOption(matchedIndex, true);
          }
          break;
      }
    }
  };

  /**
   * Focus on Tooltips button
   */
  const focusTooltip = (node) => {
    const tooltip = node?.querySelector('button');
    tooltip && tooltip.focus();
  };

  const setHighlightedOptionRef = (element) => {
    highlightedOptionRef.current = element;
    if (element) {
      focusTooltip(element);
      setSelectedId(element.id);
    }
  };

  // --- Rendering subcomponents ---

  const renderSeparator = (option, index, isHighlighted) => {
    const { name: groupName, extraClass, tooltip } = option;

    return (
      <li
        key={`${groupName}_${index}`} // group names aren't unique, "Closed" or "After Hours" may appear multiple times
        id={`${uniqueId}_${groupName}_${index}`}
        className={cn(`responsive-listbox__options-group-header ${extraClass}`)}
        role='separator'
        aria-label={groupName}
        data-group-name={groupName}
        ref={isHighlighted ? setHighlightedOptionRef : null}
      >
        <span className='label'>{groupName}</span>
        {tooltip && isExpanded && (
          <ToolTip interactive={followCursor} followCursor={interactive} placement='left-end'>
            {tooltip}
          </ToolTip>
        )}
      </li>
    );
  };

  const renderOption = (option, index, isHighlighted) => {
    const { disabled, label: optionLabel, name: optionName, value: optionValue, extraClass } = option;

    return (
      <li
        key={optionValue}
        id={`${uniqueId}_${optionValue}`}
        className={cn(extraClass, {
          disabled,
          highlight: isHighlighted && !disabled,
        })}
        data-value={optionValue}
        data-name={optionName}
        role='option'
        aria-selected={!disabled && isHighlighted ? 'true' : 'false'}
        aria-disabled={disabled}
        onMouseEnter={!disabled ? handleHighlight(index) : null}
        onClick={!disabled ? handleSelect : null}
        ref={isHighlighted ? setHighlightedOptionRef : null}
      >
        {optionLabel}
      </li>
    );
  };

  useClickOutside(
    {
      container: clickOutsideRef,
      handler: handleClickOutside,
      listenerConditional: isExpanded,
    },
    [isExpanded]
  );

  // --- Render methods ---

  const handleResponsiveListBox = () => {
    const asteriskValue = asterisk || required ? 'required' : selectedLabel;
    const ariaLabelSelectedRequiredValue = selectedLabel?.length > 0 ? selectedLabel : asteriskValue;
    const ariaLabel = `${fullLabel || label} ${ariaLabelSelectedRequiredValue}`;

    return (
      <>
        <Button
          plain
          buttonRef={togglerRef}
          aria-haspopup='listbox'
          aria-label={ariaLabel}
          aria-expanded={isExpanded ? 'true' : 'false'}
          id={`${uniqueId}__toggler`}
          name={name}
          className='responsive-listbox__toggler dropdown'
          onMouseDown={handleToggle}
          onKeyDown={handleKeyDown}
          type='button'
        >
          {selectedLabel}
        </Button>

        <div
          ref={dropdownRef}
          className={cn('responsive-listbox__dropdown', {
            hidden: isExpanded,
            'responsive-listbox__dropdown--full-height': timeAndDatePickerFullHeight,
          })}
        >
          <div className='responsive-listbox__mobile-header'>
            {showGuidedError && renderGuidedError()}
            {!showGuidedError && <span className='responsive-listbox__mobile-label'>{mobileLabel || label}</span>}
            <Button
              plain
              tabIndex='-1'
              type='button'
              className='responsive-listbox__close'
              aria-label={i18n('location_search_close_cta')}
              onClick={hideDropdown}
            >
              {i18n('location_search_close_cta')}
            </Button>
          </div>

          {showGuidedError && renderGuidedError()}

          <span
            className='scroll-up'
            data-direction='up'
            aria-hidden='true'
            onMouseEnter={handleMouseEnterLeaveScroll}
            onMouseLeave={handleMouseEnterLeaveScroll}
          />
          <ul
            ref={listRef}
            data-is-time-selector='true'
            role='listbox'
            tabIndex='-1'
            aria-labelledby={`${uniqueId}__toggler`}
            aria-activedescendant={selectedId}
            className='responsive-listbox__list'
            onScroll={handleScroll}
            onKeyDown={handleKeyDown}
          >
            {utils.isArrayNotEmpty(flatOptions) &&
              flatOptions.map((option, index) =>
                option.isSeparator
                  ? renderSeparator(option, index, highlightedIndex === index)
                  : renderOption(option, index, highlightedIndex === index)
              )}
          </ul>
          <span
            className='scroll-down'
            data-direction='down'
            aria-hidden='true'
            onMouseEnter={handleMouseEnterLeaveScroll}
            onMouseLeave={handleMouseEnterLeaveScroll}
          />
        </div>
      </>
    );
  };

  const handleCustomCombobox = () => (
    <>
      <div
        className={cn('country-selector__select', {
          'country-selector__expanded-outline': isExpanded,
        })}
        ref={togglerRef}
        aria-haspopup='listbox'
        aria-controls={`${uniqueId}__toggler`}
        aria-expanded={isExpanded ? 'true' : 'false'}
        aria-autocomplete='list'
        aria-labelledby={`${uniqueId}__input-label`}
        id={`${uniqueId}__input`}
        role={type}
        onMouseDown={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <label id={`${uniqueId}__input-label`} className='country-selector__label'>
          {i18n('country')}:
          <span id='selected_country' className='country-selector__value'>
            {currentPageTitle}
          </span>
        </label>
      </div>

      <ul
        ref={listRef}
        role='listbox'
        tabIndex='-1'
        id={`${uniqueId}__toggler`}
        aria-activedescendant={selectedId}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
      >
        {utils.isArrayNotEmpty(flatOptions) &&
          flatOptions.map((option, index) =>
            option.isSeparator
              ? renderSeparator(option, index, highlightedIndex === index)
              : renderOption(option, index, highlightedIndex === index)
          )}
      </ul>
    </>
  );

  // --- Main return ---

  return (
    <div ref={ref}>
      <div
        ref={clickOutsideRef}
        className={cn({
          'responsive-listbox': !countrySelector,
          'country-selector__container': countrySelector,
          disabled: props.disabled,
          required,
          expanded: !props.disabled && isExpanded,
          'force-mobile': !countrySelector && breakpoint.isMobile,
        })}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {countrySelector ? handleCustomCombobox() : handleResponsiveListBox()}
      </div>
    </div>
  );
});

ResponsiveListbox.propTypes = propTypes;

const Listbox = ResponsiveListbox;

const ListboxField = (props) =>
  !props.countrySelector ? (
    <FieldControl id={uuid()} {...props} dropdown type='listbox'>
      <Listbox />
    </FieldControl>
  ) : (
    <Listbox id={uuid()} {...props} type='combobox' />
  );

export default ListboxField;
